import { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '@/shared/lib/auth';
import { PageHeader } from '@/shared/components/page-header';
import { SelectableListLayout } from '@/shared/components/selectable-list-layout';
import { Button } from '@/shared/ui/button';
import { Pagination } from '@/shared/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import {
  fetchAgendaCampaignsForClient,
  readAgendaCache,
} from '../api/use-campaign-agenda';
import {
  AGENDA_CHANNEL_FILTERS,
  AGENDA_FREQ_FILTERS,
  AGENDA_WINDOW_FILTERS,
} from '../model/filters';
import { filterClientCampaigns } from '../model/group-by-client';
import type { AgendaWindow } from '../model/next-run';
import type { AgendaCampaign } from '../model/types';
import { AgendaCampaignCard } from './agenda-campaign-card';

const PAGE_SIZE = 25;
const AGENDA_QUERY_KEY = ['campaigns-admin', 'agenda', 'full'] as const;

interface LocationState {
  clientName?: string;
  filters?: {
    channel?: string;
    frequency?: string;
    window?: AgendaWindow;
    includeInactive?: boolean;
  };
}

interface AgendaCatalogResult {
  campaigns: AgendaCampaign[];
  fetchedAt: number;
  fromCache: boolean;
}

export function CampaignAgendaClientPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { clientId: clientIdParam = '' } = useParams<{ clientId: string }>();
  const clientId = decodeURIComponent(clientIdParam);
  const location = useLocation();
  const state = (location.state as LocationState | null) ?? null;

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [channel, setChannel] = useState(state?.filters?.channel ?? 'all');
  const [frequency, setFrequency] = useState(state?.filters?.frequency ?? 'all');
  const [timeWindow, setTimeWindow] = useState<AgendaWindow>(state?.filters?.window ?? 'today');
  const [includeInactive, setIncludeInactive] = useState(state?.filters?.includeInactive ?? false);

  // Catálogo já carregado na lista (React Query) ou cache local — sem iniciar varredura aqui.
  const catalog =
    queryClient.getQueryData<AgendaCatalogResult>(AGENDA_QUERY_KEY) ??
    (() => {
      const cached = readAgendaCache();
      return cached
        ? { campaigns: cached.campaigns, fetchedAt: cached.fetchedAt, fromCache: true }
        : null;
    })();

  const detailQuery = useQuery({
    queryKey: ['campaigns-admin', 'agenda', 'client', clientId],
    queryFn: () => fetchAgendaCampaignsForClient(clientId),
    staleTime: 60_000,
    enabled: user?.role === 'Admin' && Boolean(clientId) && !catalog,
  });

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(timer);
  }, [search]);

  const filterOpts = useMemo(
    () => ({
      search: debouncedSearch,
      channel,
      frequency,
      window: timeWindow,
      includeInactive,
    }),
    [debouncedSearch, channel, frequency, timeWindow, includeInactive],
  );

  const rawCampaigns = useMemo(() => {
    if (catalog?.campaigns) {
      return catalog.campaigns.filter((c) => c.clientId === clientId);
    }
    return detailQuery.data?.campaigns ?? [];
  }, [catalog?.campaigns, detailQuery.data?.campaigns, clientId]);

  const clientName =
    state?.clientName ??
    rawCampaigns[0]?.clientName ??
    detailQuery.data?.clientName ??
    'Cliente';

  const campaigns = useMemo(
    () => filterClientCampaigns(rawCampaigns, clientId, filterOpts),
    [rawCampaigns, clientId, filterOpts],
  );

  const passbookIds = useMemo(
    () => new Set(campaigns.map((c) => c.passbookId || c.id_passbook).filter(Boolean)),
    [campaigns],
  );
  const showPassbookBadge = passbookIds.size > 1;

  useEffect(() => {
    setPageIndex(0);
  }, [debouncedSearch, channel, frequency, timeWindow, includeInactive]);

  const pageRows = useMemo(() => {
    const start = pageIndex * PAGE_SIZE;
    return campaigns.slice(start, start + PAGE_SIZE);
  }, [campaigns, pageIndex]);

  if (!user || user.role !== 'Admin') {
    return <Navigate to="/login" replace />;
  }
  if (!clientId) return <Navigate to="/observabilidade?aba=campanhas" replace />;

  return (
    <>
      <PageHeader
        title={clientName}
        hideMetaOnMobile
        actions={
          <Button variant="outline" onClick={() => navigate('/observabilidade?aba=campanhas')}>
            <ArrowLeft className="size-4" />
            Voltar à agenda
          </Button>
        }
      />

      <SelectableListLayout
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por nome da campanha…"
        selectAllId="agenda-client-select-all"
        itemCount={pageRows.length}
        allOnPageSelected={false}
        someOnPageSelected={false}
        onToggleAllOnPage={() => {}}
        hideSelection
        withFloatingBar={false}
        isLoading={!catalog && detailQuery.isLoading}
        isEmpty={campaigns.length === 0}
        emptyTitle="Nenhuma campanha neste filtro"
        emptyDescription="Ajuste os filtros ou volte à agenda para escolher outro cliente."
        filtersRow={
          <div className="flex flex-wrap items-center gap-2">
            <Select value={timeWindow} onValueChange={(v) => setTimeWindow(v as AgendaWindow)}>
              <SelectTrigger className="w-auto min-w-[10rem] gap-1.5 pr-3">
                <SelectValue placeholder="Janela" />
              </SelectTrigger>
              <SelectContent>
                {AGENDA_WINDOW_FILTERS.map((f) => (
                  <SelectItem key={f.value} value={f.value}>
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={channel} onValueChange={setChannel}>
              <SelectTrigger className="w-auto min-w-[9.5rem] gap-1.5 pr-3">
                <SelectValue placeholder="Canal" />
              </SelectTrigger>
              <SelectContent>
                {AGENDA_CHANNEL_FILTERS.map((f) => (
                  <SelectItem key={f.value} value={f.value}>
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={frequency} onValueChange={setFrequency}>
              <SelectTrigger className="w-auto min-w-[10rem] gap-1.5 pr-3">
                <SelectValue placeholder="Frequência" />
              </SelectTrigger>
              <SelectContent>
                {AGENDA_FREQ_FILTERS.map((f) => (
                  <SelectItem key={f.value} value={f.value}>
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={includeInactive ? 'all' : 'active'}
              onValueChange={(v) => setIncludeInactive(v === 'all')}
            >
              <SelectTrigger className="w-auto min-w-[9rem] gap-1.5 pr-3">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Só ativas</SelectItem>
                <SelectItem value="all">Incluir inativas</SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
        footer={
          campaigns.length > PAGE_SIZE ? (
            <Pagination
              page={pageIndex}
              pageSize={PAGE_SIZE}
              total={campaigns.length}
              onPageChange={setPageIndex}
            />
          ) : null
        }
      >
        {pageRows.map((campaign, index) => (
          <li key={campaign._id}>
            <AgendaCampaignCard
              campaign={campaign}
              index={index}
              showPassbookBadge={showPassbookBadge}
              onRuns={() => navigate(`/observabilidade?aba=disparos&campanha=${encodeURIComponent(campaign._id)}`)}
              onInspect={() => {
                const pb = campaign.passbookId || campaign.id_passbook;
                if (pb) navigate(`/loja/${pb}/campanhas/automatizadas`);
              }}
            />
          </li>
        ))}
      </SelectableListLayout>
    </>
  );
}
